/**
 * API keys - where a Premium customer mints and revokes their own credentials.
 *
 * Two things drive the whole design of this page.
 *
 * First, the raw key exists exactly once. The server stores a hash and has no
 * endpoint that can show the value again, so the reveal dialog is the single
 * moment it is available. That dialog therefore says so plainly, offers a copy
 * button, and asks for an explicit acknowledgement instead of a close icon
 * that could be hit by accident.
 *
 * Second, the page has to be honest to someone who cannot use it. Free and
 * Standard accounts reach it and see what the API is, what it costs, and what
 * the limits are, rather than a locked door. That follows the same rule as the
 * analytics pages: paywall the tools, never the subject.
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControlLabel, Checkbox,
  IconButton, Snackbar, Alert as MuiAlert, CircularProgress, Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, StatCard, EmptyState, ConfirmDialog } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate, fmtNumber } from '../../utils/format';
import { listKeys, createKey, updateKeyScopes, revokeKey } from '../../services/apiKeys';

// What each scope actually lets a key read, in the customer's words rather
// than the endpoint's. A scope list with no explanation is a list of guesses.
const SCOPE_COPY = {
  'read:prices': { title: 'Prices', desc: 'Local market price observations' },
  'read:fx': { title: 'Exchange rates', desc: 'Naira rates, market and official' },
  'read:markets': { title: 'Markets', desc: 'The market and state directory' },
  'read:commodities': { title: 'Commodities', desc: 'The commodity catalogue' },
};

function ScopePicker({ selected, onToggle, palette, recipes }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {Object.entries(SCOPE_COPY).map(([id, copy]) => (
        <FormControlLabel
          key={id}
          control={
            <Checkbox
              size="small"
              checked={selected.includes(id)}
              onChange={() => onToggle(id)}
              sx={recipes.checkboxSx}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: palette.text }}>
                {copy.title}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: palette.textMuted }}>
                {copy.desc}
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', mb: 0.75, ml: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
        />
      ))}
    </Box>
  );
}

function ScopeChips({ scopes, palette }) {
  if (!scopes?.length) return <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted }}>None</Typography>;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {scopes.map((s) => (
        <Chip
          key={s}
          label={SCOPE_COPY[s]?.title || s}
          size="small"
          sx={{
            height: 19, fontSize: '0.62rem', fontWeight: 600,
            bgcolor: palette.surface, color: palette.textSecondary,
            border: `1px solid ${palette.cardBorder}`,
          }}
        />
      ))}
    </Box>
  );
}

/** What Free and Standard see. No key list, but the whole offer stated. */
function NotOnThisPlan({ palette, recipes, plan }) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ ...recipes.iconTileSx, flexShrink: 0 }}>
            <Icon icon="ph:plugs" width={18} height={18} color={palette.accent} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: palette.text }}>
              The API is part of Premium
            </Typography>
            <Typography sx={{ fontSize: '0.88rem', color: palette.textSecondary, mt: 0.75, lineHeight: 1.6, maxWidth: 620 }}>
              You are on the {plan} plan. Premium adds a read API over the same data you
              see here, so you can pull prices, exchange rates and the catalogue straight
              into your own systems. You create the keys yourself, up to five at a time,
              and can revoke any of them without our help.
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2.5, mb: 3 }}>
              {[
                ['60', 'requests a minute'],
                ['100,000', 'requests a month'],
                ['5', 'keys at a time'],
              ].map(([n, label]) => (
                <Box key={label}>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1.15rem', color: palette.text }}>
                    {n}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: palette.textMuted }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/app/subscription"
                variant="contained"
                disableElevation
                sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
              >
                See plans
              </Button>
              <Button
                component={RouterLink}
                to="/developers"
                variant="outlined"
                sx={{
                  textTransform: 'none', fontWeight: 600,
                  borderColor: palette.cardBorder, color: palette.text,
                }}
              >
                Read the documentation
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ApiKeys() {
  const { user, isAdmin } = useAuth();
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();

  const canUseApi = isAdmin || user?.plan === 'Premium';

  const { data, isLoading, error } = useQuery({
    queryKey: ['api-keys'],
    queryFn: listKeys,
    enabled: canUseApi,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(Object.keys(SCOPE_COPY));
  const [formError, setFormError] = useState('');

  // Holds the one and only sighting of a raw key.
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);

  const [toRevoke, setToRevoke] = useState(null);
  const [editing, setEditing] = useState(null);   // the key being re-scoped
  const [editScopes, setEditScopes] = useState([]);
  const [editError, setEditError] = useState('');
  const [toast, setToast] = useState(null);

  const keys = data?.keys || [];
  const active = data?.activeCount ?? 0;
  const maxKeys = data?.maxKeys ?? 5;
  const atKeyLimit = active >= maxKeys;

  const createMutation = useMutation({
    mutationFn: () => createKey(label.trim(), scopes),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setCreateOpen(false);
      setLabel('');
      setScopes(Object.keys(SCOPE_COPY));
      setCopied(false);
      setRevealed(created);
    },
    onError: (e) => setFormError(e?.message || 'Could not create the key.'),
  });

  const scopesMutation = useMutation({
    mutationFn: () => updateKeyScopes(editing.id, editScopes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setEditing(null);
      setToast({ severity: 'success', msg: 'Updated. The change applies to the next request.' });
    },
    onError: (e) => setEditError(e?.message || 'Could not change what this key may read.'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => revokeKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setToRevoke(null);
      setToast({ severity: 'success', msg: 'Key revoked. Any call using it now fails.' });
    },
    onError: (e) => {
      setToRevoke(null);
      setToast({ severity: 'error', msg: e?.message || 'Could not revoke the key.' });
    },
  });

  const toggleScope = (s) =>
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const toggleEditScope = (s) =>
    setEditScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const openEdit = (k) => {
    setEditScopes(k.scopes || []);
    setEditError('');
    setEditing(k);
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealed.key);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (an insecure origin, a permission
      // prompt). Say so rather than showing a tick that did nothing.
      setToast({ severity: 'warning', msg: 'Could not reach the clipboard. Select the key and copy it by hand.' });
    }
  };

  const header = (
    <PageHeader
      eyebrow="Developers"
      title="API keys"
      subtitle="Credentials for reading Farmgate data from your own systems"
      breadcrumbs={[{ label: 'Home', path: '/app/dashboard' }, { label: 'API keys' }]}
      actions={
        canUseApi ? (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/developers"
              variant="outlined"
              size="small"
              startIcon={<Icon icon="ph:book-open" width={15} height={15} />}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: palette.cardBorder, color: palette.text }}
            >
              Documentation
            </Button>
            <Tooltip title={atKeyLimit ? `You have ${maxKeys} active keys. Revoke one to create another.` : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  disabled={atKeyLimit}
                  onClick={() => { setFormError(''); setCreateOpen(true); }}
                  startIcon={<Icon icon="ph:plus" width={15} height={15} />}
                  sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
                >
                  New key
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : null
      }
    />
  );

  if (!canUseApi) {
    return (
      <Box>
        {header}
        <NotOnThisPlan palette={palette} recipes={recipes} plan={user?.plan || 'Free'} />
      </Box>
    );
  }

  return (
    <Box>
      {header}

      {/* Spend and headroom, from the request log rather than the cache. */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Requests this month"
            value={fmtNumber(data?.requestsThisMonth ?? 0)}
            sub={data ? `of ${fmtNumber(data.requestsPerMonth)}, resets ${fmtDate(data.resetsOn)}` : ' '}
            icon={<Icon icon="ph:arrows-left-right" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Remaining"
            value={fmtNumber(data?.remaining ?? 0)}
            sub="requests before the quota resets"
            icon={<Icon icon="ph:gauge" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Rate limit"
            value={`${data?.ratePerMinute ?? 60}/min`}
            sub="per key, measured over a rolling minute"
            icon={<Icon icon="ph:speedometer" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Active keys"
            value={`${active} of ${maxKeys}`}
            sub={atKeyLimit ? 'Revoke one to create another' : 'You can create more'}
            icon={<Icon icon="ph:key" width={20} height={20} />}
            tone={atKeyLimit ? 'warning' : 'accent'}
          />
        </Grid>
      </Grid>

      <Card>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Your keys</Typography>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} sx={{ color: palette.accent }} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <MuiAlert severity="error">{error?.message || 'Could not load your keys.'}</MuiAlert>
          </Box>
        )}

        {!isLoading && !error && keys.length === 0 && (
          <EmptyState
            icon={<Icon icon="ph:key" width={24} height={24} />}
            title="No keys yet"
            description="Create a key to start reading Farmgate data from your own systems. The key value is shown once, when you create it."
            actionLabel="New key"
            onAction={() => { setFormError(''); setCreateOpen(true); }}
          />
        )}

        {!isLoading && !error && keys.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={recipes.headCellSx}>Name</TableCell>
                  <TableCell sx={recipes.headCellSx}>Key</TableCell>
                  <TableCell sx={recipes.headCellSx}>Can read</TableCell>
                  <TableCell sx={recipes.headCellSx} align="right">This month</TableCell>
                  <TableCell sx={recipes.headCellSx}>Last used</TableCell>
                  <TableCell sx={recipes.headCellSx}>Created</TableCell>
                  <TableCell sx={recipes.headCellSx} align="right">Manage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => {
                  const revoked = k.status === 'revoked';
                  return (
                    <TableRow key={k.id} sx={{ ...recipes.bodyRowSx, opacity: revoked ? 0.55 : 1 }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: palette.text }}>
                            {k.label}
                          </Typography>
                          {revoked && (
                            <Chip
                              label="REVOKED"
                              size="small"
                              sx={{
                                height: 18, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.05em',
                                bgcolor: palette.dangerBg, color: palette.danger,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: palette.textSecondary }}>
                          {k.prefix}
                          <Box component="span" sx={{ color: palette.textMuted }}>…</Box>
                        </Typography>
                      </TableCell>
                      <TableCell><ScopeChips scopes={k.scopes} palette={palette} /></TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.82rem', color: palette.text }}>
                          {fmtNumber(k.requestsThisMonth ?? 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: k.lastUsedAt ? palette.textSecondary : palette.textMuted }}>
                          {k.lastUsedAt ? fmtDate(k.lastUsedAt) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary }}>
                          {fmtDate(k.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {!revoked && (
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                            <Tooltip title="Change what this key may read">
                              <IconButton
                                size="small"
                                aria-label={`Change what the key named ${k.label} may read`}
                                onClick={() => openEdit(k)}
                                sx={{ color: palette.textMuted, '&:hover': { color: palette.accent, bgcolor: palette.accentBg } }}
                              >
                                <Icon icon="ph:sliders-horizontal" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke this key">
                              <IconButton
                                size="small"
                                aria-label={`Revoke the key named ${k.label}`}
                                onClick={() => setToRevoke(k)}
                                sx={{ color: palette.textMuted, '&:hover': { color: palette.danger, bgcolor: palette.dangerBg } }}
                              >
                                <Icon icon="ph:prohibit" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: 2.5, px: 0.5 }}>
        <Icon icon="ph:info" width={16} height={16} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, lineHeight: 1.6, maxWidth: 760 }}>
          Send your key in the <Box component="span" sx={{ fontFamily: MONO }}>X-API-Key</Box> header. Revoked
          keys stop working immediately, and stay listed so their usage history keeps its
          subject. Counts here come from the request log, so they survive a restart.
        </Typography>
      </Box>

      {/* ---- Create ---- */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 480, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Create an API key</DialogTitle>
        <DialogContent dividers>
          {formError && <MuiAlert severity="error" sx={{ mb: 2 }}>{formError}</MuiAlert>}
          <TextField
            label="Name"
            size="small"
            fullWidth
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="Something you will recognise later, like 'pricing sheet' or 'staging'"
            inputProps={{ maxLength: 60 }}
            InputLabelProps={{ sx: recipes.labelSx }}
            InputProps={{ sx: recipes.controlSx }}
          />

          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: palette.text, mt: 2.5, mb: 0.5 }}>
            What this key may read
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: palette.textSecondary, mb: 1 }}>
            Narrow it to what the integration actually needs. A key that can read less is
            worth less if it leaks.
          </Typography>
          <ScopePicker
            selected={scopes}
            onToggle={toggleScope}
            palette={palette}
            recipes={recipes}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { setFormError(''); createMutation.mutate(); }}
            disabled={createMutation.isPending || !label.trim() || scopes.length === 0}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {createMutation.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- The one sighting of the raw key ---- */}
      <Dialog
        open={!!revealed}
        // No onClose: closing by clicking away or pressing Escape would lose a
        // value that cannot be recovered. The acknowledge button is the only exit.
        disableEscapeKeyDown
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 560, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Copy your key now</DialogTitle>
        <DialogContent dividers>
          <MuiAlert severity="warning" sx={{ mb: 2.5 }}>
            This is the only time this value is shown. We store a hash of it, so we cannot
            show it again or recover it for you. If you lose it, revoke the key and create
            another.
          </MuiAlert>

          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 1.5, borderRadius: '10px',
              bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`,
            }}
          >
            <Typography
              component="code"
              sx={{
                fontFamily: MONO, fontSize: '0.82rem', color: palette.text,
                wordBreak: 'break-all', flex: 1, userSelect: 'all',
              }}
            >
              {revealed?.key}
            </Typography>
            <Button
              size="small"
              onClick={copyKey}
              startIcon={<Icon icon={copied ? 'ph:check' : 'ph:copy'} width={15} height={15} />}
              sx={{
                textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap',
                color: copied ? palette.accent : palette.text,
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Box>

          <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, mt: 2, lineHeight: 1.6 }}>
            Keep it somewhere your application can read and a person cannot: an environment
            variable or a secret store, not a repository. Send it as the{' '}
            <Box component="span" sx={{ fontFamily: MONO }}>X-API-Key</Box> header.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { setRevealed(null); setCopied(false); }}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            I have copied it
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Change what a key may read ---- */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 480, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          What {editing?.label} may read
        </DialogTitle>
        <DialogContent dividers>
          {editError && <MuiAlert severity="error" sx={{ mb: 2 }}>{editError}</MuiAlert>}
          <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary, mb: 1.75, lineHeight: 1.6 }}>
            The key itself does not change, so anything already using it keeps working.
            Removing a scope takes effect on the very next request, so take one away only
            when you know nothing depends on it.
          </Typography>
          <ScopePicker
            selected={editScopes}
            onToggle={toggleEditScope}
            palette={palette}
            recipes={recipes}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditing(null)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { setEditError(''); scopesMutation.mutate(); }}
            disabled={scopesMutation.isPending || editScopes.length === 0}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {scopesMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!toRevoke}
        onClose={() => setToRevoke(null)}
        onConfirm={() => revokeMutation.mutate(toRevoke.id)}
        title={`Revoke ${toRevoke?.label || 'this key'}?`}
        description="Anything using this key stops working immediately, and it cannot be turned back on. The key stays listed so its usage history keeps its subject."
        confirmLabel="Revoke key"
        icon="ph:prohibit"
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && (
          <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </MuiAlert>
        )}
      </Snackbar>
    </Box>
  );
}

export default ApiKeys;
